import {observer} from 'mobx-react'
import {ChangeEventHandler, useEffect, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useStore} from 'rest/stores'
import * as Yup from 'yup'
import {
  AuthService,
  MerchantAddDto,
  MerchantQueueAddDto,
  MerchantService,
} from 'rest'
import {Form, Input, Submit} from 'components/form'
import {Button} from 'components/generic'
import {Heading} from 'components/template/Heading'
import {FileInput} from 'components/form/FileInput'
import {getBase64ImageDimensions} from 'utils/base64'
import normalizeUrl from 'utils/normalizeUrl'
import MerchantStatus from 'components/MerchantStatus'
import useUser from 'hooks/useUser'
import {toast} from 'react-hot-toast'
import APP_CONFIG from 'constants/clientConfigs'

const {siteName} = APP_CONFIG[process.env.REACT_APP_CLIENT_ID]

export const Add = observer(() => {
  const {user} = useUser()
  let localMerchant = user?.store
  const {AdvertiserStore, MerchantStore, ContactStore} = useStore()
  const navigate = useNavigate()

  const schema = Yup.object().shape({
    name: Yup.string().required(),
    url: Yup.string().required(),
    firstName: Yup.string(),
    lastName: Yup.string(),
    email: Yup.string().email().required(),
    stripe_payment_email: Yup.string()
      .transform(v => (v === '' || v == null ? undefined : v))
      .optional()
      .email('Must be a valid email'),
  })

  const localUser = JSON.parse(localStorage.getItem('user'))

  const defaultValues = {
    name: localMerchant?.name || '',
    url: localMerchant?.website_url || 'https://',
    logoUrl: localMerchant?.logo_url || '',
    subscriptionId: 1,
    firstName: localMerchant?.contact_first_name || '',
    lastName: localMerchant?.contact_last_name || '',
    email: localMerchant?.contact_email || localUser?.email,
    stripe_payment_email: localMerchant?.stripe_payment_email || '',
  }
  const merchant = localMerchant?.id ? localMerchant : null

  const [form, setForm] = useState<Record<string, any>>({
    name: defaultValues.name,
    url: defaultValues.url,
    firstName: defaultValues.firstName,
    lastName: defaultValues.lastName,
    email: defaultValues.email,
    stripe_payment_email: defaultValues.stripe_payment_email,
  })
  const [logo, setLogo] = useState<string>(null)
  const [logoError, setLogoError] = useState<string>(null)
  const ref = useRef<HTMLImageElement>(null)

  const handleFileChange = async base64String => {
    const {width, height} = await getBase64ImageDimensions(base64String)
    if (width !== 120 && height !== 40) {
      return setLogoError('Logo image should be 120x40px image.')
    }

    setLogoError(null)
    setLogo(base64String)
  }
  const handleRemoveLogo = () => {
    setLogo('')
  }
  const handleSubmit = async data => {
    const payloadMerchantAdd: MerchantAddDto & {
      logoBase64Content: string
    } = {
      advertiserId: AdvertiserStore.advertiser?.id || 1,
      categoryId: null,
      name: form.name,
      url: normalizeUrl(form.url),
      logoUrl: form?.logoUrl,
      subscriptionId: form.subscriptionId,
      logoBase64Content: logo,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      stripe_payment_email: form.stripe_payment_email?.trim() || null,
    }

    // ADD
    if (!merchant) {
      MerchantService.postApiMerchantAdd(payloadMerchantAdd).then(res => {
        const localUser = JSON.parse(localStorage.getItem('user'))

        localUser.store = res
        localStorage.setItem('user', JSON.stringify(localUser))

        toast.success('Your store page was created successfully')
        AuthService.getAuthUser()
        navigate('/payment')
      })
    } else {
      const payloadMerchantQueue: MerchantQueueAddDto = {
        ...payloadMerchantAdd,
        merchantId: merchant.id,
      }
      if (logo === '') {
        delete payloadMerchantQueue.logoBase64Content
        payloadMerchantQueue.logoUrl = ''
      }
      return await MerchantStore.queueMerchant(payloadMerchantQueue).then(
        res => {
          if (res) {
            toast.success('Changes saved successfully')
            void AuthService.getAuthUser()
          }
        }
      )
    }
  }

  useEffect(() => {
    ;(async () => await ContactStore.getContact())()
    //eslint-disable-next-line
  }, [])

  const onChange: ChangeEventHandler<HTMLInputElement> = e => {
    setForm(form => ({...form, [e.target.name]: e.target.value}))
  }

  return (
    <div>
      {!merchant?.id && (
        <Heading title={`ADD YOUR STORE TO ${siteName}`.toUpperCase()} />
      )}
      <h1 className="py-4 text-xl font-semibold leading-8 tracking-tight">
        YOUR STORE INFORMATION
      </h1>

      <MerchantStatus loading={false} />

      <div className="flex">
        <Form
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col sm:min-w-[360px] max-w-2xl">
          <section className="bg-white rounded-md shadow p-4">
            <div className="">
              <Input name="name" onChange={onChange} label="Store Name" />
            </div>
            <div className="mt-6">
              <Input name="url" onChange={onChange} label="Store Website URL" />
            </div>
            <div className="mt-6">
              <FileInput
                id="logo"
                name="logoBase64Content"
                label="Store Logo (Optional)"
                placeholder="Select: A 120x40px image file ..."
                onChange={handleFileChange}
              />
              {logoError && (
                <span className="text-xs text-red-700">{logoError}</span>
              )}
              {((logo !== '' && merchant?.logo_url) || logo) && (
                <img
                  ref={ref}
                  src={logo || merchant?.logo_url}
                  alt="logo"
                  width="120"
                  height="40"
                  className="mt-4"
                />
              )}
              {((logo !== '' && merchant?.logo_url) || logo) && (
                <Button
                  onClick={handleRemoveLogo}
                  className="text-xs text-red-600 bg-gray-500 my-2 w-48">
                  Remove the logo
                </Button>
              )}
            </div>
          </section>

          <h1 className="py-4 pt-8 text-xl font-semibold leading-8 tracking-tight">
            CONTACT INFORMATION
          </h1>

          <section className="bg-white rounded-md shadow p-4">
            <div className="flex">
              <div className="grow">
                <Input
                  name="firstName"
                  onChange={onChange}
                  label="First Name"
                />
              </div>
              <div className="grow ml-4">
                <Input name="lastName" onChange={onChange} label="Last Name" />
              </div>
            </div>
            <div className="mt-6">
              <Input
                name="email"
                onChange={onChange}
                label="Email Address"
                type="text"
              />
            </div>
            <div className="mt-6">
              <Input
                name="stripe_payment_email"
                onChange={onChange}
                label="Stripe payment email"
                type="email"
                autocomplete="email"
                placeholder="Only if different from the email above"
              />
              <p className="mt-1 text-xs text-gray-500">
                If you pay in Stripe with a different email than your contact
                email, enter it here so subscriptions and payments match your
                account.
              </p>
            </div>
          </section>

          <Submit onClick={handleSubmit} className="primary my-6">
            {merchant?.id ? 'SAVE CHANGES' : 'CREATE YOUR STORE'}
          </Submit>
        </Form>
        <div className="grow bg-[url('assets/mascot.svg')] bg-no-repeat bg-center hidden md:block opacity-70" />
      </div>
    </div>
  )
})
